import {
  DatePicker, Form, Input, Select,
} from 'antd';
import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import {
  addAttachmentsData,
  addBookingQuestionAndAnswersData,
  selectBookingAttachmentsData,
  selectBookingMedicareCardData,
  selectBookingQuestionAndAnswersData,
  updateBookingData,
  updateBookingMedicareCardData,
} from '../../redux/slices/booking';
import FooterNavigator from '../../components/FooterNavigator';
import {
  StepError, StepLead, StepPage, StepSection,
} from '../../components/StepPanel';
import FileField from './FileField';
import { scrollToFirstError } from '../../utils/form';
import { CLIENT_DETAILS, REVIEW } from '../../constants/routes';
import { DATE_WITH_DASH } from '../../constants/time-formats';
import { COVER_LABELS, CoverType, MedicareCard } from '../../model/MedicareCard';
import { DVACardType } from '../../model/DVACardType';
import { HealthSummaryQuestionMapping } from '../../model';
import { UPLOAD_STATUS } from '../../model/Attachment';
import { needsMedicareClaimingTerm } from '../../services/booking';
import analytics from '../../infra/analytics';
import { MEDICARE_AND_HEALTH as PAGE_NAME, PAGE_VIEW_EVENT, SITE_NAME } from '../../constants/analytics';

const { TextArea } = Input;

const COVER_OPTIONS = [
  { value: CoverType.MEDICARE, title: 'Medicare card', detail: 'We lodge your Medicare claim for you.' },
  { value: CoverType.DVA, title: 'DVA card', detail: 'Department of Veterans\' Affairs — Gold, White or Orange.' },
  { value: CoverType.MEDICARE_NOT_WITH_ME, title: 'I have a Medicare card but not with me', detail: 'Give it to us before your appointment and we can still claim your rebate.' },
  { value: CoverType.NONE, title: 'No Medicare or DVA card', detail: 'You can still book — you\'ll be charged the private fee.' },
];

const DVA_COLOURS = [
  { value: DVACardType.GOLD, label: 'Gold' },
  { value: DVACardType.WHITE, label: 'White' },
  { value: DVACardType.ORANGE, label: 'Orange' },
];

// A DVA file number, e.g. NX123456 or QSM12345.
const DVA_FILE_NUMBER = /^[NVQSWT]([A-Z ][0-9]{1,6}|[A-Z]{2}[0-9]{1,5}|[A-Z]{3}[0-9]{1,4})[A-Z]?$/;

type CoverRadiosProps = {
  value?: CoverType;
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: CoverType) => void;
};

// The mockup's option rows; Form.Item passes value and onChange.
const CoverRadios: FC<CoverRadiosProps> = ({ value, onChange }) => (
  <div className="opts" role="radiogroup" aria-label="What cover do you have?">
    {COVER_OPTIONS.map((option) => (
      <label key={option.value} className={`opt-row${value === option.value ? ' on' : ''}`} htmlFor={`cover-${option.value}`}>
        <input
          id={`cover-${option.value}`}
          type="radio"
          name="coverType"
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange?.(option.value)}
        />
        <span className="tx">
          <b>{option.title}</b>
          <span>{option.detail}</span>
        </span>
      </label>
    ))}
  </div>
);

const MedicareAndHealth: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const medicareCard = useAppSelector(selectBookingMedicareCardData) as Partial<MedicareCard>;
  const questionAndAnswers = useAppSelector(selectBookingQuestionAndAnswersData);
  const { healthSummary, medicalSupporting } = useAppSelector(selectBookingAttachmentsData);
  const [cover, setCover] = useState<CoverType | undefined>(medicareCard.coverType);
  // Leaving mid-upload would drop the file, so Continue waits for it.
  const isUploading = [healthSummary, medicalSupporting].some((file) => file?.status === UPLOAD_STATUS.UPLOADING);
  const [triedWhileUploading, setTriedWhileUploading] = useState(false);

  useEffect(() => {
    analytics.logPageViewEvent(PAGE_VIEW_EVENT, {
      site: SITE_NAME,
      page_name: PAGE_NAME,
    });
  }, []);

  // Fill the form with anything already given, e.g. after Back.
  useEffect(() => {
    const { expiry, ...card } = medicareCard;
    form.setFieldsValue({
      ...card,
      expiry: expiry ? moment(expiry, DATE_WITH_DASH) : undefined,
      gpName: questionAndAnswers.gpName,
      healthConcern: questionAndAnswers.healthConcern,
      allergies: questionAndAnswers.allergies,
      currentMedication: questionAndAnswers.currentMedication,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleUploadHealthSummary = useCallback((attachment) => dispatch(addAttachmentsData({ healthSummary: attachment })), [dispatch]);
  const handleUploadMedicalSupporting = useCallback((attachment) => dispatch(addAttachmentsData({ medicalSupporting: attachment })), [dispatch]);

  const submit = useCallback(async () => {
    if (isUploading) {
      setTriedWhileUploading(true);
      return false;
    }
    try {
      const {
        coverType, medicareCardNumber, medicareCardIRN, expiry, dva,
        gpName, healthConcern, allergies, currentMedication,
      } = await form.validateFields();

      // Keep only the details that belong to the chosen cover.
      const card: MedicareCard = { coverType };
      if (coverType === CoverType.MEDICARE) {
        Object.assign(card, {
          medicareCardNumber,
          medicareCardIRN,
          expiry: expiry.clone().endOf('month').format(DATE_WITH_DASH),
        });
      }
      if (coverType === CoverType.DVA) {
        card.dva = { hasDept: true, cardType: dva.cardType, cardNumber: dva.cardNumber };
      }
      dispatch(updateBookingData({ medicareCard: card }));

      dispatch(addBookingQuestionAndAnswersData({
        cover: COVER_LABELS[coverType as CoverType],
        gpName: gpName.trim(),
        // Cleared answers are removed, not left at their old value.
        healthConcern: healthConcern?.trim() || undefined,
        allergies: allergies?.trim() || undefined,
        currentMedication: currentMedication?.trim() || undefined,
      }));
      return true;
    } catch (e) {
      scrollToFirstError(form, e);
      return false;
    }
  }, [dispatch, form, isUploading]);

  const hasMedicareClaim = needsMedicareClaimingTerm(cover);

  return (
    <StepPage>
      <h2>Medicare &amp; health information</h2>
      <StepLead>Your cover decides your rebate, so we ask before showing you the final estimate.</StepLead>

      <Form
        layout="vertical"
        form={form}
        scrollToFirstError
        onValuesChange={({ coverType }) => {
          if (!coverType) return;
          setCover(coverType);
          // The fee estimate follows the cover as soon as it is picked.
          dispatch(updateBookingMedicareCardData({ coverType }));
        }}
      >
        <StepSection>
          <h3>Cover</h3>
          <Form.Item
            name="coverType"
            label="What cover do you have?"
            required
            rules={[{ required: true, message: 'Please choose the cover you have.' }]}
          >
            <CoverRadios />
          </Form.Item>

          {cover === CoverType.MEDICARE && (
            <div className="grid3">
              <Form.Item
                label="Medicare card number"
                required
                name="medicareCardNumber"
                normalize={(value?: string) => value?.replace(/\s/g, '')}
                rules={[
                  { required: true, message: 'Please enter your Medicare card number.' },
                  { pattern: /^\d{10}$/, message: 'Please enter the 10 digits on your Medicare card.' },
                ]}
              >
                <Input inputMode="numeric" maxLength={12} placeholder="0000 00000 0" />
              </Form.Item>
              <Form.Item
                label="IRN"
                required
                name="medicareCardIRN"
                extra="The number beside your name on the card."
                rules={[
                  { required: true, message: 'Please enter your IRN.' },
                  { pattern: /^[1-9]$/, message: 'Please enter the single digit beside your name.' },
                ]}
              >
                <Input inputMode="numeric" maxLength={1} />
              </Form.Item>
              <Form.Item
                label="Expiry"
                required
                name="expiry"
                rules={[{ required: true, message: 'Please enter the expiry on your card.' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="MM/YYYY" format="MM/YYYY" picker="month" />
              </Form.Item>
            </div>
          )}

          {cover === CoverType.DVA && (
            <div className="grid2">
              <Form.Item
                label="DVA card colour"
                required
                name={['dva', 'cardType']}
                rules={[{ required: true, message: 'Please choose the colour of your DVA card.' }]}
              >
                <Select placeholder="Please select colour" options={DVA_COLOURS} />
              </Form.Item>
              <Form.Item
                label="DVA file number"
                required
                name={['dva', 'cardNumber']}
                normalize={(value?: string) => value?.toUpperCase()}
                rules={[
                  { required: true, message: 'Please enter your DVA file number.' },
                  { pattern: DVA_FILE_NUMBER, message: 'Please enter the file number as it appears on your card, e.g. NX123456.' },
                ]}
              >
                <Input />
              </Form.Item>
            </div>
          )}

          {cover === CoverType.NONE && (
            <div className="notice n-warn" role="status">
              <p><b>You&apos;ll be charged the private fee for this appointment.</b></p>
              <p>Without a Medicare or DVA card we cannot lodge a claim, so no rebate applies. The estimate has been updated.</p>
            </div>
          )}
        </StepSection>

        <StepSection>
          <h3>Referral &amp; supporting documents</h3>
          <Form.Item label="GP referral or health summary" htmlFor="file-referral">
            <FileField id="file-referral" file={healthSummary} onChange={handleUploadHealthSummary} />
            <p className="upload-hint">
              {cover === CoverType.NONE
                ? 'You can still book without a referral. Without Medicare or DVA cover, the private fee applies either way.'
                : 'A valid GP referral is needed for a Medicare rebate. You can still book without one — you\'ll be charged the private fee.'}
            </p>
          </Form.Item>
          <Form.Item
            htmlFor="file-other"
            label={(
              <>
                {'Other documents '}
                <span className="opt">(pathology, imaging, reports)</span>
              </>
            )}
          >
            <FileField id="file-other" file={medicalSupporting} onChange={handleUploadMedicalSupporting} />
          </Form.Item>
          <Form.Item
            name="gpName"
            label={HealthSummaryQuestionMapping.gpName}
            required
            extra={'If you don\'t have one, write "no referring doctor".'}
            rules={[{ required: true, whitespace: true, message: 'Please enter your referring doctor and clinic, or write "no referring doctor".' }]}
          >
            <Input />
          </Form.Item>
        </StepSection>

        <StepSection>
          <h3>For your clinician</h3>
          <Form.Item name="healthConcern" label={HealthSummaryQuestionMapping.healthConcern}>
            <TextArea rows={3} placeholder="A short summary of your health concerns." />
          </Form.Item>
          <div className="grid2">
            <Form.Item name="allergies" label={HealthSummaryQuestionMapping.allergies}>
              <Input placeholder="e.g. Penicillin" />
            </Form.Item>
            <Form.Item name="currentMedication" label={HealthSummaryQuestionMapping.currentMedication}>
              <Input placeholder="e.g. Sertraline 50mg daily" />
            </Form.Item>
          </div>
        </StepSection>
      </Form>

      {hasMedicareClaim && (
        <p className="notice n-info">
          {'If you are eligible for a Medicare rebate for this service, we will lodge the Medicare patient claim on your behalf within '}
          <b>3 business days</b>
          {' of you attending your appointment. Your rebate may vary based on your eligibility.'}
        </p>
      )}

      {isUploading && triedWhileUploading && (
        <StepError role="alert">Your file is still uploading. Please wait for it to finish, then continue.</StepError>
      )}

      <FooterNavigator
        submit={submit}
        next={REVIEW}
        back={CLIENT_DETAILS}
        disabled={isUploading}
        hint={isUploading ? 'Waiting for your file to upload' : undefined}
      />
    </StepPage>
  );
};

export default MedicareAndHealth;
